import { useMemo } from 'react';

import { useGetTeamsSlice } from './useGetTeamsSlice';

import {
  getTotalPercentageComplete,
  getTotalPercentageCompleteForEachCourse,
} from '../components/admin/GeneralReport/TableSummaryReport/TableSummaryReport.utils';

import {
  Learner,
  LearnerData,
} from '../components/admin/GeneralReport/TableSummaryReport/TableSummaryReport.types';

/**
 * Get the learner course progress
 * @param learnerData
 * @returns The following learner data: totalLearnerCount, learnersWhoHaventStartedCount,
 * learnersWhoHaventCompletedCount, learnersWhoHaveCompletedCount, learnersWhoHaventStarted,
 * learnersWhoHaventCompleted, learnersWhoHaveCompleted, formattedLearners
 */
export const useLearnerCourseProgress = (learnerData: Optional<IReportData>): LearnerData => {
  const { activeCourses } = useGetTeamsSlice();

  return useMemo(() => {
    if (!learnerData) {
      return {
        totalLearnerCount: 0,
        learnersWhoHaventStartedCount: 0,
        learnersWhoHaventCompletedCount: 0,
        learnersWhoHaveCompletedCount: 0,
        formattedLearners: [],
      };
    }

    const allLearners = learnerData.rows;

    let learnersWhoHaventStartedCount = 0;
    let learnersWhoHaventCompletedCount = 0;
    let learnersWhoHaveCompletedCount = 0;

    const formattedLearners: Learner[] = [];

    allLearners.forEach(({ firstName, lastName, email, courseProgresses }: IReportRow) => {
      const noCoursesAssigned = !courseProgresses || courseProgresses.length === 0;
      const totalPercentageComplete = getTotalPercentageComplete(courseProgresses, activeCourses);
      const courseProgress = getTotalPercentageCompleteForEachCourse(courseProgresses);

      const formattedLearner = {
        name: `${firstName} ${lastName}`,
        email,
        totalPercentageComplete: noCoursesAssigned ? undefined : totalPercentageComplete,
        courseProgress: courseProgress,
      };

      if (!noCoursesAssigned) {
        if (totalPercentageComplete === 0) {
          learnersWhoHaventStartedCount++;
        }

        if (totalPercentageComplete < 100 && totalPercentageComplete > 0) {
          learnersWhoHaventCompletedCount++;
        }

        if (totalPercentageComplete === 100) {
          learnersWhoHaveCompletedCount++;
        }
      }

      formattedLearners.push(formattedLearner);
    });

    return {
      totalLearnerCount: allLearners.length,
      learnersWhoHaventStartedCount,
      learnersWhoHaventCompletedCount,
      learnersWhoHaveCompletedCount,
      formattedLearners,
    };

    // We don't need to recalculate when activeCourses changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnerData]);
};
